/**
 * As transições entre as telas do portal da paciente (10/10/2026), aprovadas
 * pelo Fábio a partir de um protótipo: cada tipo de navegação com o seu
 * movimento, todos pela View Transitions API, com a curva do onboarding
 * (`cadastro/transicao.ts`).
 *
 * - **Aba ↔ aba**: a tela sai para um lado e a nova entra pelo outro, na
 *   direção da barra, com cabeçalho e barra parados (a barra anima ao vivo a
 *   troca em líquido).
 * - **Aprofundar** (abrir ferramenta, checkpoint, perfil, agendamento…): a
 *   tela nova entra pela direita por cima, e a de baixo recua e escurece um
 *   pouco — a pilha dos apps nativos.
 * - **Voltar**: o inverso, um pouco mais rápido. Vale para o botão e para o
 *   voltar do navegador ou do gesto do celular.
 * - **Capa**: tocar no cartão de uma revista ou de um vídeo faz a capa crescer
 *   até virar o topo da tela que abre (`marcarCapa`).
 * - **Modal** (11/10/2026): o perfil, aberto pela engrenagem, sobe de baixo
 *   cobrindo tudo, sem a barra de abas, e desce ao fechar — a tela de trás
 *   recua e escurece. Dentro dele, as telas de cada item se aprofundam como
 *   as outras.
 *
 * Quem decide é este módulo, interceptando o `navigate` do roteador de dados
 * (`instalarTransicoes`, em `app/router.tsx`): as telas continuam chamando
 * `navigate` como sempre. Interceptar o roteador também resolve o que a
 * troca de aba já tinha esbarrado — o portal monta as rotas com `useRoutes`,
 * e ali o `useNavigate` descarta o `viewTransition` (A136). O navegador
 * simples do React Router chama `router.navigate` por baixo, e é aí que o
 * movimento entra.
 *
 * O tipo vai num atributo da raiz (`data-navegacao`), lido pelo `eu.css`, e
 * sai quando a transição termina. Ficam de fora: substituições (redirects),
 * o formulário de adesão (que tem a própria animação de janela),
 * navegações fora de `/eu` e as que só mudam a busca. Sem suporte no
 * navegador ou com "reduzir movimento", troca direto.
 */
type Navegar = (para: any, opcoes?: any) => Promise<void>;

interface Roteador {
  navigate: Navegar;
  state: { location: { pathname: string } };
  subscribe: (ouvinte: (estado: { location: { pathname: string }; historyAction: string }) => void) => () => void;
}

export type TipoDeNavegacao = 'aba-direita' | 'aba-esquerda' | 'avanco' | 'retorno' | 'capa' | 'modal-abre' | 'modal-fecha';

/**
 * As abas, na ordem da barra: é ela que dá a direção da troca. O perfil fica
 * de fora — não está na barra; abre pela engrenagem do cabeçalho, como modal.
 */
const ORDEM_DAS_ABAS = ['/eu', '/eu/care', '/eu/content', '/eu/community', '/eu/chat'];

/**
 * O perfil e as telas dos itens dele: a "modal" das configurações. Entrar na
 * família sobe de baixo; sair desce; dentro dela, aprofunda e volta.
 */
const PERFIL = '/eu/profile';
const FAMILIA_DO_PERFIL = new Set([
  PERFIL,
  '/eu/myProfile',
  '/eu/maternityPhase',
  '/eu/gestationData',
  '/eu/sharedAccess',
  '/eu/marketingPreferences',
  '/eu/appFeedback',
  '/eu/terms',
  '/eu/dependentes',
]);
const FORMULARIO = '/eu/adhesion-form';
const ATRIBUTO = 'navegacao';
/** Rede de segurança: a marca sai quando a transição termina; isto é só se nada terminar. */
const LIMITE_MS = 4000;
/** Quantos quadros esperar a transição começar depois da navegação. */
const QUADROS_ATE_COMECAR = 90;

const normalizar = (caminho: string) => caminho.replace(/\/+$/, '') || '/';
const ehAba = (caminho: string) => ORDEM_DAS_ABAS.includes(caminho);
export const ehDoPerfil = (caminho: string) => FAMILIA_DO_PERFIL.has(normalizar(caminho));
const doPortal = (caminho: string) => caminho === '/eu' || caminho.startsWith('/eu/');

const suporta = () => typeof document !== 'undefined' && typeof (document as Document & { startViewTransition?: unknown }).startViewTransition === 'function';
const movimentoReduzido = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const transicaoAtiva = () => (document as Document & { activeViewTransition?: { finished: Promise<void> } | null }).activeViewTransition ?? null;
const indiceDoHistorico = () => (window.history.state as { idx?: number } | null)?.idx;

let caminhoAtual = '';
let indiceAtual: number | undefined;
let marcaAtual = 0;
let limite: ReturnType<typeof setTimeout> | undefined;
let proximaComCapa = false;
/** O caminho de cada entrada do histórico desta sessão, pelo índice. */
const caminhosDoHistorico = new Map<number, string>();
/** De onde ela abriu o perfil: é para lá que o "fechar" volta. */
let origemDoPerfil: { caminho: string; indice: number | undefined } | null = null;
const ouvintes = new Set<(tipo: TipoDeNavegacao | null) => void>();

/** O tipo de uma navegação de `de` para `para`. Nulo = sem transição. Exportado para os testes. */
export function tipoDaNavegacao(de: string, para: string, opcoes: { voltando?: boolean; capa?: boolean } = {}): TipoDeNavegacao | null {
  const a = normalizar(de);
  const b = normalizar(para);
  if (a === b || !doPortal(a) || !doPortal(b)) return null;
  // A janela do formulário vem por cima com a própria animação; a tela de trás nem muda.
  if (a === FORMULARIO || b === FORMULARIO) return null;
  // A modal do perfil: entrar sobe, sair desce — vale também para o voltar do
  // navegador. Dentro dela, voltar ao perfil é voltar.
  if (!ehDoPerfil(a) && ehDoPerfil(b)) return 'modal-abre';
  if (ehDoPerfil(a) && !ehDoPerfil(b)) return 'modal-fecha';
  if (b === PERFIL) return 'retorno';
  if (ehAba(a) && ehAba(b)) return ORDEM_DAS_ABAS.indexOf(b) > ORDEM_DAS_ABAS.indexOf(a) ? 'aba-direita' : 'aba-esquerda';
  if (opcoes.voltando) return 'retorno';
  if (opcoes.capa) return 'capa';
  // Para uma aba, vindo de uma tela empilhada, é voltar; para a tela-mãe
  // (`/eu/care-tool/diario` → `/eu/care-tool`) também; de resto, aprofundar.
  if (ehAba(b) || a.startsWith(`${b}/`)) return 'retorno';
  return 'avanco';
}

function desmarcar(marca: number) {
  if (marca !== marcaAtual) return;
  delete document.documentElement.dataset[ATRIBUTO];
}

/** Espera a transição que a navegação vai abrir, e desmarca quando ela acabar. */
function desmarcarAoTerminar(marca: number) {
  // Toque rápido: a transição anterior ainda pode estar no ar, e o fim dela não é o fim desta.
  const anterior = transicaoAtiva();
  let quadros = 0;
  const procurar = () => {
    const t = transicaoAtiva();
    if (t && t !== anterior) t.finished.finally(() => desmarcar(marca));
    else if (++quadros < QUADROS_ATE_COMECAR) requestAnimationFrame(procurar);
    else desmarcar(marca);
  };
  requestAnimationFrame(procurar);
}

function marcar(tipo: TipoDeNavegacao) {
  const marca = ++marcaAtual;
  document.documentElement.dataset[ATRIBUTO] = tipo;
  clearTimeout(limite);
  limite = setTimeout(() => desmarcar(marca), LIMITE_MS);
  desmarcarAoTerminar(marca);
  for (const ouvinte of ouvintes) ouvinte(tipo);
}

/** Para o shell saber que tipo de navegação aconteceu (a rolagem depende disso). */
export function ouvirNavegacao(ouvinte: (tipo: TipoDeNavegacao | null) => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/**
 * O cartão que ela tocou vira a capa que cresce até o topo da tela seguinte.
 * Chame no clique, antes de navegar: o nome precisa estar no elemento quando
 * o navegador fotografa a tela de saída. O nome sai sozinho no fim, para não
 * haver dois elementos com o mesmo nome na próxima transição.
 */
export function marcarCapa(elemento: HTMLElement | null) {
  if (!elemento || !suporta() || movimentoReduzido()) return;
  // Um nome só por vez na tela: o cartão tocado antes perde o dele.
  document.querySelectorAll<HTMLElement>('[data-capa-marcada]').forEach((el) => {
    el.style.removeProperty('view-transition-name');
    delete el.dataset.capaMarcada;
  });
  elemento.style.setProperty('view-transition-name', 'eu-capa');
  elemento.dataset.capaMarcada = 'true';
  proximaComCapa = true;
  setTimeout(() => {
    elemento.style.removeProperty('view-transition-name');
    delete elemento.dataset.capaMarcada;
    proximaComCapa = false;
  }, LIMITE_MS);
}

/**
 * Fecha a modal do perfil voltando à tela de onde ela a abriu — no lugar do
 * histórico em que estava, rolagem incluída, por mais itens que tenha aberto
 * lá dentro (o voltar de cada item empilha o perfil de novo). Sem origem
 * conhecida (link direto, recarregar), vai para a tela inicial.
 */
export function fecharPerfil(navegar: { (delta: number): void; (para: string): void }) {
  const origem = origemDoPerfil;
  const atual = indiceDoHistorico();
  const voltaPeloHistorico =
    origem && origem.indice !== undefined && atual !== undefined && atual > origem.indice && caminhosDoHistorico.get(origem.indice) === origem.caminho;
  if (voltaPeloHistorico) navegar(origem!.indice! - atual!);
  else navegar(origem && doPortal(origem.caminho) ? origem.caminho : '/eu');
}

function caminhoDe(para: unknown): string | null {
  if (typeof para === 'string') return para.startsWith('/') ? para.split(/[?#]/)[0] : null;
  if (para && typeof para === 'object' && typeof (para as { pathname?: unknown }).pathname === 'string') return (para as { pathname: string }).pathname;
  return null;
}

/** Liga as transições no roteador de dados. Chamado uma vez, onde o roteador nasce. */
export function instalarTransicoes(roteador: Roteador) {
  if (typeof window === 'undefined') return;
  caminhoAtual = normalizar(roteador.state.location.pathname);
  indiceAtual = indiceDoHistorico();
  if (indiceAtual !== undefined) caminhosDoHistorico.set(indiceAtual, caminhoAtual);
  // Voltar e avançar do navegador (POP): o React Router repete a transição da
  // ida; daqui sai só o tipo. Decidido aqui, e não num ouvinte de `popstate`,
  // porque o roteador trata o `popstate` antes de qualquer ouvinte nosso e o
  // caminho de origem já se perderia. Este ouvinte é registrado antes do
  // `RouterProvider` (que abre a transição), então roda antes dele. Quando o
  // roteador avisa, o histórico já está na entrada nova.
  roteador.subscribe((estado) => {
    const de = caminhoAtual;
    const indiceAnterior = indiceAtual;
    caminhoAtual = normalizar(estado.location.pathname);
    indiceAtual = indiceDoHistorico();
    if (indiceAtual !== undefined) caminhosDoHistorico.set(indiceAtual, caminhoAtual);
    if (!ehDoPerfil(de) && ehDoPerfil(caminhoAtual)) origemDoPerfil = { caminho: de, indice: indiceAnterior };
    if (estado.historyAction !== 'POP' || de === caminhoAtual || !suporta() || movimentoReduzido()) return;
    const voltando = indiceAtual !== undefined && indiceAnterior !== undefined ? indiceAtual < indiceAnterior : true;
    const tipo = tipoDaNavegacao(de, caminhoAtual, { voltando });
    // Avançar de novo pelo histórico até uma tela empilhada é aprofundar.
    if (tipo) marcar(!voltando && tipo === 'retorno' ? 'avanco' : tipo);
  });

  const original = roteador.navigate.bind(roteador);
  roteador.navigate = (para: any, opcoes?: any) => {
    const destino = caminhoDe(para);
    const comCapa = proximaComCapa;
    proximaComCapa = false;
    // Muitos botões de voltar navegam para a tela de trás em vez de `navigate(-1)`:
    // ir para onde a entrada anterior do histórico estava também é voltar.
    const voltando = !!destino && indiceAtual !== undefined && caminhosDoHistorico.get(indiceAtual - 1) === normalizar(destino);
    const tipo = destino && !opcoes?.replace && suporta() && !movimentoReduzido() ? tipoDaNavegacao(caminhoAtual, destino, { capa: comCapa, voltando }) : null;
    if (!tipo) return original(para, opcoes);
    marcar(tipo);
    return original(para, { ...opcoes, viewTransition: true });
  };
}
